
import { observable, computed, action } from 'mobx';
import { FormField } from '../../../WebGiving/components/form-field';
import {
	requiredValidator,
	lengthValidator,
	combineValidators,
	fieldValidationSuccess,
	FieldValidationFailure,
	RuleType,
	createValidatorFunc,
} from '../../../WebGiving/validation/validation-rules';
import { ModelMetadata, PayerOnboardingMerchantInfo } from '../../payer-onboarding-generated';
import { getCardBrandByInput, getCardBrandsFromKeys } from '../../../WebGiving/utils/card-utils';
import { validateLuhn } from '../../../WebGiving/utils/luhn-validation';

const cardNumberInvalidMessage = 'This card number is not valid';
const cvvInvalidMessage = 'This CVV is not valid';

export class CardPaymentMethodViewModel {
	cardNumber: FormField<string>;

	cardExpiryDate: FormField<string>;

	cvv: FormField<string>;

	zipCode: FormField<string>;

	supportedCardBrandKeys: string[];

	@computed
	get cardExpirationMonth() {
		return (this.cardExpiryDate.value.split('/')[0] || '').trim();
	}

	@computed
	get cardExpirationYear() {
		return (this.cardExpiryDate.value.split('/')[1] || '').trim();
	}

	@computed
	get cardBrand() {
		return getCardBrandByInput(this.cardNumber.value);
	}

	@observable
	zipCodeLabel: string;

	constructor(merchant: PayerOnboardingMerchantInfo, postalCodeCommonName: string) {
		this.zipCodeLabel = postalCodeCommonName || 'Zip code';
		this.supportedCardBrandKeys = merchant.SupportedCardBrandKeys;
		const supportedCardBrandNames = getCardBrandsFromKeys(this.supportedCardBrandKeys).map(x => x.friendlyName).join(', ');

		this.cardNumber = new FormField<string>('', combineValidators(requiredValidator('Please enter a card number'), createValidatorFunc((value: string) => {
			const cardBrand = this.cardBrand;

			if (!cardBrand) {
				return new FieldValidationFailure(RuleType.Custom, cardNumberInvalidMessage);
			}

			if (this.supportedCardBrandKeys.indexOf(cardBrand.key) === -1) {
				return new FieldValidationFailure(RuleType.Custom, `${merchant.TradingName} only supports ${supportedCardBrandNames}`);
			}

			if (!cardBrand.cardNumberRegex.test(value) || (cardBrand.shouldLuhn && !validateLuhn(value))) {
				return new FieldValidationFailure(RuleType.Custom, cardNumberInvalidMessage);
			}

			return fieldValidationSuccess;
		}, RuleType.Custom)));

		this.cvv = new FormField<string>('', combineValidators(requiredValidator('Please enter a CVV'), createValidatorFunc((value: string) => {
			if (!this.cardBrand) {
				// cannot validate the field because no cvvRegex
				// when there's no cardBrand, validation will fail on the card number field
				return fieldValidationSuccess;
			}

			if (!this.cardBrand.cvvRegex.test(value)) {
				return new FieldValidationFailure(RuleType.Custom, cvvInvalidMessage);
			}

			return fieldValidationSuccess;
		}, RuleType.Custom)));

		this.cardExpiryDate = new FormField<string>('', combineValidators(requiredValidator('Please enter an expiration date'), createValidatorFunc((value: string) => {
			const [month, year] = value.split('/').map(x => x.trim());
			const parsedYear = parseInt(year);
			const parsedMonth = parseInt(month);

			if (isNaN(parsedYear) || isNaN(parsedMonth) || parsedMonth > 12) {
				return new FieldValidationFailure(RuleType.Custom, 'This expiration date is not valid');
			}

			if(parsedYear > 99) {
				return new FieldValidationFailure(RuleType.Custom, 'This expiration date is not valid');
			}

			const thisYear = new Date().getFullYear() % 100;
			const thisMonth = new Date().getMonth() + 1; // +1 because js months start with zero

			if (parsedYear < thisYear || (parsedYear === thisYear && parsedMonth < thisMonth)) {
				return new FieldValidationFailure(RuleType.Custom, 'This expiration date is in the past');
			}

			return fieldValidationSuccess;
		}, RuleType.Custom)));

		const zipCodeValidationMessage = `This ${this.zipCodeLabel.toLowerCase()} is not valid`;
		const rules = ModelMetadata.AddCreditCardPaymentMethodRequest.ZipCode.validationRules;
		const maxLength = rules && rules.length ? rules.length.parameters.max : 50;

		this.zipCode = new FormField<string>('', combineValidators(
			requiredValidator(`Please enter ${this.zipCodeLabel.toLowerCase()}`),
			lengthValidator(0, maxLength, zipCodeValidationMessage)),
		);
	}

	@action
	clearValidation() {
		[ this.cardNumber, this.zipCode, this.cardExpiryDate, this.cvv ].forEach((field) => field.resetField());
	}
}
